import React, { useEffect, useState } from "react";
import * as PropTypes from "prop-types";
import { Alert, Box, Button, Checkbox, FormControlLabel, FormGroup, TextField, Typography } from "@mui/material";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { ProvidersService } from "../../services/ProvidersService";

const creatingStripeAccountMessage = "Creating Stripe Account...";

export function PaymentsEditor({ provider, setProvider }) {
    const [stripeChecked, setStripeChecked] = useState(provider.stripeAccepted == true);
    const [cashChecked, setCashChecked] = useState(provider.cashAccepted == true);
    const [setupStripeDisabled, setSetupStripeDisabled] = useState(true);
    const [stripeAccountId, setStripeAccountId] = useState(provider.stripeId);
    const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState(false);

    useEffect(() => {
        if (stripeChecked && !provider.stripeId) {
            setStripeAccountId(creatingStripeAccountMessage);
            createStripeAccountClicked();
        }
    }, [stripeChecked]);

    useEffect(() => {
        setSetupStripeDisabled(!stripeChecked || !stripeAccountId ||
            stripeAccountId === creatingStripeAccountMessage);
    }, [stripeChecked, stripeAccountId]);

    useEffect(() => {
        if (provider.stripeId) {
            setStripeAccountId(provider.stripeId);
        }
    }, [provider]);

    // Fetch Stripe onboarding status when component mounts or provider.id changes
    useEffect(() => {
        if (!provider.id) {
            return;
        }

        ProvidersService.getStripeAccountStatus(provider.id)
            .then((status) => {
                setStripeOnboardingComplete(status.stripeOnboardingComplete === true);
            })
            .catch((error) => {
                // Treat failure as "not complete" and continue gracefully
                console.error("Failed to fetch Stripe status:", error);
                setStripeOnboardingComplete(false);
            });
    }, [provider.id]);

    function createStripeAccountClicked() {
        ProvidersService.createStripeAccount(provider.id).then((response) => {
            if (response.status === 200) {
                ProvidersService.getSingleProvider(provider.id).then((updatedProvider) => {
                    setProvider({ ...updatedProvider, stripeAccepted: true, cashAccepted: cashChecked });
                });
            }
        });
    }

    function setupStripeAccountClicked() {
        ProvidersService.getStripeAccountLink(provider.id, window.location).then((link) => {
            window.location = link.url;
        });
    }

    function handleStripeCheckboxChanged(event) {
        let updatedProvider = { ...provider };
        updatedProvider.stripeAccepted = event.target.checked;
        setProvider(updatedProvider);
        setStripeChecked(event.target.checked);
    }

    function handleCashCheckboxChanged(event) {
        let updatedProvider = { ...provider };
        updatedProvider.cashAccepted = event.target.checked;
        setProvider(updatedProvider);
        setCashChecked(event.target.checked);
    }

    return (
        <FormGroup>
                    <FormControlLabel control={<Checkbox checked={cashChecked} onChange={handleCashCheckboxChanged} />} label={"Cash"} />
                    <FormControlLabel control={<Checkbox checked={stripeChecked} onChange={handleStripeCheckboxChanged} />} label={"Stripe"} />
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                        Set up Stripe to accept credit card payments and offer prepaid services — prepaid bookings protect you from no-shows.
                    </Typography>
                    {stripeChecked && !stripeOnboardingComplete && (
                        <Alert severity="info" icon={<TaskAltIcon />} sx={{ mb: 2 }}>
                            Stripe registration takes just 10–15 minutes and unlocks secure payments, faster payouts, and a smoother experience. We strongly encourage you to complete it now.
                        </Alert>
                    )}
                    {stripeChecked && stripeOnboardingComplete && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            Stripe setup complete — you can accept credit card payments and prepaid bookings.
                        </Alert>
                    )}
                    <Box display={"grid"} style={{ borderRadius: "10px", borderColor: "gray", border: "solid 1px", background: "white" }}>
                        <TextField disabled={!stripeChecked} label={"Stripe Account Id"} value={stripeAccountId} style={{ margin: "10px", marginLeft: "5px" }} InputLabelProps={{ shrink: true }} />
                        <Button variant={setupStripeDisabled ? "outlined" : "contained"} style={{ margin: "10px", marginLeft: "5px" }}
                            disabled={setupStripeDisabled}
                            onClick={setupStripeAccountClicked}>Setup Stripe Account</Button>
                    </Box>
                </FormGroup>
    );
}

PaymentsEditor.propTypes = {
    provider: PropTypes.shape({
        id: PropTypes.string.isRequired,
        stripeAccepted: PropTypes.bool,
        cashAccepted: PropTypes.bool,
        stripeId: PropTypes.string,
    }).isRequired,
    setProvider: PropTypes.func.isRequired,
};
